"use client";
import { Clock, MapPin } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import React from "react";
import { IPackageRecord } from "../interface/IPackageData.interface";
import { months } from "@/data/tourPackagesData";
import { useSession } from "next-auth/react";
import { useModal } from "@/core/context/ModalContext";
import { showErrorMessage } from "@/utils/toastMessage/toast.message";

interface IProps {
  activeTab: string;
  content: IPackageRecord[];
}

const ListView: React.FC<IProps> = ({ content }) => {
  const packageName = content;
  const { status } = useSession();
  const isLoggedIn = status === "authenticated";

  const { openModal } = useModal();

  const handleNotLoggedIn = () => {
    openModal("login");
    showErrorMessage("Please login to access this feature.");
  };

  return (
    <div>
      {" "}
      {packageName.map((packageItem: IPackageRecord) => (
        <div key={packageItem.id}>
          <div className="hidden md:flex mb-6 flex-row gap-2 md:gap-5">
            <Image
              src={packageItem?.image || "/assest/images/placeholder.png"}
              alt={packageItem?.name || "package-image"}
              width={500}
              height={300}
              className="h-20 w-32 rounded-xl lg:h-52 lg:w-80 lg:rounded-2xl flex-shrink-0 object-cover"
            />
            <div className="flex flex-col">
              <div className="flex md:flex-row flex-col justify-between items-start">
                <div className="md:w-[70%] w-full">
                  <p className="typography-small-body text-blue-600 inline-flex gap-1 md:mb-2 items-center justify-center">
                    <MapPin size={12} /> {packageItem?.destination}
                  </p>
                  <Link href={`/package/${packageItem?.slug}`}>
                    <h2
                      className="md:typography-sub-h2-regular sm:typography-extra-large-body text-grey-700 font-medium my-3"
                      title={packageItem?.name}
                    >
                      {packageItem?.name}
                    </h2>
                  </Link>
                  <p
                    className="typography-small-body-light text-grey-500 line-clamp-2"
                    dangerouslySetInnerHTML={{
                      __html: packageItem?.description || "",
                    }}
                  />
                </div>
                <div className="md:w-[29%] w-full md:mt-10">
                  <p className="text-grey-500 inline-flex gap-1 items-center justify-center mb-2">
                    <Clock size={17} /> {packageItem?.duration} days
                  </p>
                  <p className="typography-small-body-light text-grey-500">
                    Starting From:{" "}
                    <span className="typography-large-body font-medium text-grey-700">
                      NPR {packageItem?.current_price}
                    </span>
                  </p>
                  <div className="flex md:flex-row flex-col justify-between mt-3 gap-3">
                    {isLoggedIn ? (
                      <Link href={`/user/book/${packageItem?.id}`}>
                        <button className="typography-large-body text-white bg-primary-500 px-3.5 py-1.5 rounded-sm cursor-pointer hover:bg-primary-600">
                          Book Now
                        </button>
                      </Link>
                    ) : (
                      <div
                        onClick={handleNotLoggedIn}
                        className="typography-large-body text-white bg-primary-500 px-3.5 py-1.5 rounded-sm cursor-pointer hover:bg-primary-600 text-center"
                      >
                        Book Now
                      </div>
                    )}
                    <Link href={`/package/${packageItem?.slug}`}>
                      <button className="typography-large-body font-light text-yellow-500 px-3.5 py-1.5 rounded-sm border border-yellow-500 cursor-pointer">
                        Learn More
                      </button>
                    </Link>
                  </div>
                </div>
              </div>
              <p className="hidden md:block typography-mid-body text-grey-500 my-2">
                Availability:
                {packageItem?.availability_month}
                <span className="ml-2 inline-flex flex-wrap gap-3">
                  {months.map((month) => {
                    const available = packageItem?.availability_month?.some(
                      (m: string) => {
                        const a = m.toLowerCase().trim();
                        return (
                          a === month.toLowerCase() ||
                          a.startsWith(month.slice(0, 3).toLowerCase())
                        );
                      }
                    );
                    return (
                      <span
                        key={month}
                        className={
                          available
                            ? "font-medium text-green-500"
                            : "font-light line-through"
                        }
                      >
                        {month.slice(0, 3)}
                      </span>
                    );
                  })}
                </span>
              </p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ListView;
